/**
 * Education - Courses Page (Task 53)
 */

import { useEffect, useState, useMemo } from 'react';
import { CourseList } from '@/components/industry/shared/CourseList';
import { CourseForm } from '@/components/industry/shared/CourseForm';
import { useIndustryCourses } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { IndustryCourse } from '@/types/industry';
import { MainLayout } from "@/components/MainLayout";

export default function EducationCoursesPage() {
  useEffect(() => {
    document.title = 'Cursos | Controle Financeiro';
  }, []);

  const { courses, isLoading, createCourse, updateCourse, deleteCourse } = useIndustryCourses({
    domain: CompanyType.EDUCATION,
  });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<IndustryCourse | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract existing categories for the form
  const existingCategories = useMemo(() => {
    if (!courses) return [];
    const cats = new Set<string>();
    courses.forEach((c) => {
      const category = c.metadata?.category as string;
      if (category) cats.add(category);
    });
    return Array.from(cats).sort();
  }, [courses]);

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const installmentsAllowed = typeof data.installments === 'number' && data.installments > 1;
      const courseData = {
        name: data.name,
        price: data.price,
        description: data.description,
        max_students: data.capacity || undefined,
        duration_hours: data.duration ? Math.ceil(data.duration / 60) : undefined,
        installments_allowed: installmentsAllowed,
        max_installments: data.installments || undefined,
        status: data.is_active ? 'published' : 'draft',
        is_active: data.is_active,
        metadata: {
          duration: data.duration,
          capacity: data.capacity,
          schedule: data.schedule,
          installments: data.installments,
          category: data.category,
          description: data.description,
        },
      };

      if (editingCourse) {
        await updateCourse({ id: editingCourse.id, ...courseData });
        toast.success('Curso atualizado');
      } else {
        await createCourse(courseData);
        toast.success('Curso criado');
      }
      setIsFormOpen(false);
      setEditingCourse(undefined);
    } catch (error) {
      toast.error('Erro ao salvar curso');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
<MainLayout>
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </div>
    
    </MainLayout>);
  }

  return (
<MainLayout>
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Cursos</h1>
        <p className="text-muted-foreground">Gerencie o catálogo de cursos</p>
      </div>

      <CourseList
        courses={courses || []}
        onEdit={(course) => {
          setEditingCourse(course);
          setIsFormOpen(true);
        }}
        onDelete={async (course) => {
          if (window.confirm('Excluir curso?')) {
            await deleteCourse(course.id);
            toast.success('Curso excluído');
          }
        }}
        onCreate={() => {
          setEditingCourse(undefined);
          setIsFormOpen(true);
        }}
      />

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCourse ? 'Editar' : 'Novo'} Curso</DialogTitle>
            <DialogDescription>
              Preencha os detalhes do curso para organizar seu catálogo educacional.
            </DialogDescription>
          </DialogHeader>
          <CourseForm
            course={editingCourse}
            existingCategories={existingCategories}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsFormOpen(false);
              setEditingCourse(undefined);
            }}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>
    </div>
  
    </MainLayout>);
}
